import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Typography } from '@components/atoms/Typography/Typography';

type BrowserVideoPreviewProps = {
  stream: MediaStream | null;
  isLoading?: boolean;
  error?: Error | null;
  className?: string;
};

/**
 * Displays a video preview from a browser MediaStream
 * without requiring a Chime meeting session.
 *
 * @example
 * const { stream, error, isLoading } = useBrowserVideoPreview({ videoDeviceId });
 * <BrowserVideoPreview stream={stream} error={error} isLoading={isLoading} />
 */
export const BrowserVideoPreview = ({
  stream,
  isLoading = false,
  error = null,
  className = '',
}: BrowserVideoPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!videoRef.current || !stream) return;

    // Attach stream to video element
    videoRef.current.srcObject = stream;

    return () => {
      // Cleanup
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-surface-dark via-surface-dark to-brand-primary">
        <div className="text-center">
          <Typography type="body" size="md" className="text-white">
            Erreur d'accès à la caméra
          </Typography>
          <Typography type="body" size="sm" className="mt-2 text-white/70">
            {error.message}
          </Typography>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-surface-dark via-surface-dark to-brand-primary">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
          <Typography type="body" size="md" className="text-white">
            Chargement de la caméra...
          </Typography>
        </div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-surface-dark via-surface-dark to-brand-primary">
        <Typography type="body" size="md" className="text-white">
          Aucun flux vidéo disponible
        </Typography>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className={`absolute inset-0 h-full w-full object-cover ${className}`}
    />
  );
};
